import { Component, inject, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Capacitor } from "@capacitor/core";
import { AlertController, IonModal } from "@ionic/angular";
import { SavePassword } from "capacitor-ios-autofill-save-password";

import { AccountService } from "../../lib/account.service";
import { Modal } from "../../lib/modal";
import { AuthMethodType } from "../../lib/models/survey";
import { NavigationService } from "../../lib/navigation.service";
import { PasswordValidator } from "../../lib/password.validator";
import { FetchStatus } from "../../lib/util";
import { DbConfigService } from "../../lib/dbconfig.service";

enum AccountModalState {
  // Choose between signing up and signing in
  Initial,
  ChangePassword,
}

@Component({
  selector: "app-account-modal",
  templateUrl: "./account-modal.component.html",
  styleUrls: ["./account-modal.component.css"],
})
export class AccountModalComponent extends Modal<AccountModalState> implements OnInit {
  // Import enum for use in template
  readonly AccountModalState = AccountModalState;
  readonly AuthMethodType = AuthMethodType;
  readonly validations = PasswordValidator.validations;

  account = inject(AccountService);
  alertController = inject(AlertController);
  dbConfig = inject(DbConfigService);

  changePasswordForm!: FormGroup;
  signinDisabled = false;
  deleteAcctLink: string | URL | undefined = undefined;

  @ViewChild(IonModal) modal!: IonModal;
  getModal() {
    return this.modal;
  }
  getInitialState() {
    return AccountModalState.Initial;
  }

  ngOnInit() {
    this.changePasswordForm = new FormGroup({
      old_password: new FormControl("", [Validators.required]),
      password: new FormControl("", [PasswordValidator.checkChange]),
      confirm_password: new FormControl("", [PasswordValidator.checkConfirm]),
    });

    // Load any config values
    if (this.account.isSignedIn()) {
      this.dbConfig.getValue('delete_acct_link').subscribe(response => {
        this.deleteAcctLink = response.body.configValue;
      });
    }
  }

  override async back() {
    switch (this.state) {
      case AccountModalState.Initial: {
        await this.close();
        break;
      }
      case AccountModalState.ChangePassword: {
        this.state = AccountModalState.Initial;
        break;
      }
    }
  }

  async logout() {
    await this.account.logout();
  }

  /**
   * Open new window showing the form to request account deletion.
   */
  async deleteAccount() {
    if (this.deleteAcctLink) {
      window.open(this.deleteAcctLink, '_blank');
    }
  }

  submitChangePassword() {
    this.signinDisabled = true;

    const val = this.changePasswordForm.value as Record<string, string>;

    this.account.changePassword(
      String(val.old_password),
      String(val.password),
      this.changePasswordComplete.bind(this),
      this.changePasswordError.bind(this)
    );
  }

  async changePasswordError(err: FetchStatus) {
    let message = "";

    if (err.reason === "password-fails-requirements") {
      // Should be impossible since password is also validated client-side
      message = "Please use a different password.";
    } else {
      message = "Sorry, your password could not be changed. Please try again later.";
    }

    const alert = await this.alertController.create({
      header: "Password Change Error",
      message: message,
      buttons: ["Ok"],
    });
    await alert.present();

    // Wait for the user to clear the alert
    await alert.onDidDismiss();

    this.signinDisabled = false;
  }

  async changePasswordComplete() {
    if (Capacitor.getPlatform() === "ios") {
      if (!this.account.email || !this.changePasswordForm.value.password) {
        console.error("Unable to autosave password");
        return;
      }

      console.log("Trying to autosave", this.account.email, this.changePasswordForm.value.password);
      await SavePassword.promptDialog({
        username: this.account.email,
        password: this.changePasswordForm.value.password as string,
      });
    }

    await this.close();
    await this.account.alert("Password Updated", "Your password has successfully been changed.");
  }

  async goHome() {
    await this.account.goHome();
    await this.close();
  }

  async settings() {
    await this.close();
    NavigationService.forceTo("/settings");
  }

  /**
   * Method is tied to the didDismiss event and triggers when the
   * user dismisses the modal by either clicking on the close button
   * or by clicking a background element. Use this to reset the Account
   * Modal state to be 'Initial' so that it resets the workflow. It shouldn't
   * 'save' that the user was on say the 'change password' screen. If the
   * user exits the modal and re-enters, present the main options, not where
   * the user was when they dismissed.
   */
  modalClosed() {
    this.state = this.AccountModalState.Initial;
  }
}
